import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GraduationCap } from 'lucide-react';

/**
 * MobileProfileEvalPill
 * ----------------------------------------------------------------------------
 * Discreet, non-intrusive bottom-edge pill displayed on mobile viewports
 * (375px - 768px) when users scroll past the hero on UNIVERSITY and COUNTRY
 * pages. Hidden on desktop, hidden on the wizard itself, hidden on the
 * eligibility checker (those pages already have inline CTAs).
 *
 * Designed to coexist with StickyEligibilityCTA (which lives bottom-right)
 * by anchoring to bottom-center across the viewport width.
 */
const MobileProfileEvalPill = () => {
  const location = useLocation();
  const [show, setShow] = useState(false);

  // Eligibility for this CTA: ONLY on university profile pages. Country hub
  // pages ship their own purpose-built mobile sticky CTA bar (WhatsApp + Book
  // Counselling) — adding the pill on top creates 3 stacked CTAs at 375px.
  const path = location.pathname.replace(/\/+$/, '');
  const isUniversityPage = path.includes('/universities/');

  // Suppress when the user is already on the wizard or other lead-capture pages
  const onSuppressedRoute =
    path.endsWith('/free-profile-evaluation') ||
    path.includes('/visa-eligibility') ||
    path.includes('/book-counselling');

  const shouldMount = isUniversityPage && !onSuppressedRoute;

  useEffect(() => {
    if (!shouldMount) {
      setShow(false);
      return undefined;
    }
    // Only reveal after the user has scrolled past the hero zone (~520px)
    const onScroll = () => setShow(window.scrollY > 520);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [shouldMount]);

  if (!shouldMount) return null;

  return (
    <div
      className={`md:hidden fixed bottom-4 inset-x-4 z-[55] transition-all duration-300 ${
        show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
      data-testid="sa-mobile-profile-eval-pill"
    >
      <Link
        to="/study-abroad/free-profile-evaluation/?source=study-abroad-free-profile-evaluation-mobile-pill"
        className="flex items-center justify-center gap-2 w-full bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm py-3.5 rounded-full shadow-2xl shadow-black/40 border border-[#EAB308]/30 active:scale-[0.98] transition-transform"
        data-testid="sa-mobile-profile-eval-pill-link"
      >
        <GraduationCap className="w-4 h-4" />
        Free Profile Evaluation
      </Link>
    </div>
  );
};

export default MobileProfileEvalPill;
